import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/pfc";
import { Store, Instagram, Globe, ArrowLeft, ShoppingBag } from "lucide-react";

export default function VendorStorefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/marketplace/vendors/${slug}`);
        setData(data);
        document.title = `${data.vendor.business_name} — PFC Marketplace`;
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="bg-ink-900 min-h-screen py-24 text-center text-white/40">Loading…</div>;
  if (!data) return <div className="bg-ink-900 min-h-screen py-24 text-center text-white/40">Vendor not found.</div>;

  const { vendor, products } = data;

  return (
    <div className="bg-ink-900 min-h-screen text-white" data-testid="vendor-storefront">
      {/* Banner */}
      <div className="relative bg-gradient-to-b from-black via-ink-900 to-ink-900 border-b border-white/10">
        {vendor.banner && (
          <div className="absolute inset-0 opacity-30">
            <img src={vendor.banner} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
          <Link to="/marketplace" className="inline-flex items-center gap-2 text-white/60 hover:text-pfc-gold text-xs uppercase tracking-[0.3em] font-sub mb-6">
            <ArrowLeft size={14} /> All Vendors
          </Link>
          <div className="flex items-start gap-6 flex-wrap">
            {vendor.logo ? (
              <img src={vendor.logo} alt={vendor.business_name} className="h-24 w-24 object-contain bg-black/40 p-2 border border-white/10" />
            ) : (
              <div className="h-24 w-24 bg-white/5 grid place-items-center text-pfc-gold border border-white/10">
                <Store size={36} />
              </div>
            )}
            <div className="flex-1 min-w-[260px]">
              {vendor.is_official && (
                <div className="text-[10px] tracking-[0.4em] uppercase text-pfc-gold font-sub mb-2">Official Collection</div>
              )}
              <h1 className="font-display text-3xl sm:text-4xl">{vendor.business_name}</h1>
              {vendor.tagline && <p className="text-white/70 text-base mt-2">{vendor.tagline}</p>}
              <div className="flex items-center gap-4 mt-4">
                {vendor.instagram && (
                  <a href={`https://instagram.com/${vendor.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="text-white/60 hover:text-pfc-gold inline-flex items-center gap-1 text-sm" data-testid="vendor-instagram">
                    <Instagram size={14} /> {vendor.instagram}
                  </a>
                )}
                {vendor.website && (
                  <a href={vendor.website} target="_blank" rel="noreferrer" className="text-white/60 hover:text-pfc-gold inline-flex items-center gap-1 text-sm" data-testid="vendor-website">
                    <Globe size={14} /> Website
                  </a>
                )}
              </div>
            </div>
          </div>
          {vendor.description && (
            <p className="text-white/70 mt-6 max-w-3xl text-sm leading-relaxed whitespace-pre-line">{vendor.description}</p>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
        <h2 className="font-display text-2xl mb-8">Products</h2>
        {products.length === 0 ? (
          <div className="text-white/40 py-20 text-center">No products yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendor-products-grid">
            {products.map((p) => (
              <Link
                key={p.product_id}
                to={`/marketplace/product/${p.product_id}`}
                className="block group border border-white/10 bg-ink-800/40 overflow-hidden hover:border-pfc-gold transition"
                data-testid={`vendor-product-${p.product_id}`}
              >
                <div className="aspect-square bg-white/5 overflow-hidden">
                  {(p.images || [])[0] ? (
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-white/30"><ShoppingBag size={48} /></div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg text-white group-hover:text-pfc-gold transition">{p.title}</h3>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-pfc-gold font-display text-xl">${p.price.toFixed(2)}</span>
                    <span className="text-[11px] uppercase tracking-[0.3em] text-white/50 font-sub">View →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
